/** @jsxRuntime classic */
/** @jsx jsx */
import { useMemo } from 'react';
import { jsx } from '@emotion/react';
import { PaymentDetailAndIcon } from './PaymentDetailAndIcon';

import { getXLastChars } from '../../../lib/utils';
import { PaymentMethodTypeEnum } from '../../../types/entities/payment';
import { i18n } from '../../../lib/i18n';
import { EPaymentIcons } from '../../../atoms/PaymentMethod';
import { ETypographyColor } from '../../../types';
import { DEFAULT_LINE_HEIGHT, CHECK_LAST_CHARS, GIFT_CARD_LAST_CHARS, DEFAULT_ICON_SIZE } from '../utils/consts';
import { IPaymentMethodDisplayInfo } from '../types';

interface PaymentMethodProps {
	state: IPaymentMethodDisplayInfo;
	noIcons?: boolean;
	color?: ETypographyColor;
}

const LABELS = i18n.PaymentMethodTypeEnum;

export const PaymentMethodMapper = ({ state, color, noIcons = false }: PaymentMethodProps) => {
	const { paymentMethodId, stripePaymentMethodId } = state;

	const rawType: PaymentMethodTypeEnum = state.paymentType ?? (state as any).method ?? PaymentMethodTypeEnum.OTHER;
	const normalizedType = rawType.replace(' ', '-').toLowerCase();

	const pmId = stripePaymentMethodId ?? paymentMethodId ?? '';

	const { text, icon, iconSize } = useMemo((): { text: string; icon: string; iconSize: number } => {
		const ccBrand = state.ccBrand || '';
		const last4 = state.ccLast4 || '';

		let text: string;
		let icon: string;
		let iconSize = DEFAULT_ICON_SIZE;

		switch (normalizedType) {
			case PaymentMethodTypeEnum.CARD_ON_TERMINAL:
			case PaymentMethodTypeEnum.CARD: {
				text = `${noIcons ? `${ccBrand} ` : ''}${last4}`;
				icon = ccBrand;
				iconSize = DEFAULT_LINE_HEIGHT;
				break;
			}
			case PaymentMethodTypeEnum.CHECK: {
				const methodId = pmId ? `#${pmId.slice(-CHECK_LAST_CHARS)}` : '';
				text = `${LABELS[PaymentMethodTypeEnum.CHECK]} ${methodId}`;
				icon = noIcons ? '' : EPaymentIcons[PaymentMethodTypeEnum.CHECK];
				break;
			}
			case PaymentMethodTypeEnum.GIFT_CARD: {
				const methodId = pmId ? `#${getXLastChars(pmId, GIFT_CARD_LAST_CHARS)}` : '';
				text = `${LABELS[PaymentMethodTypeEnum.GIFT_CARD]} ${methodId}`;
				icon = noIcons ? '' : EPaymentIcons[PaymentMethodTypeEnum.GIFT_CARD];
				break;
			}
			case PaymentMethodTypeEnum.ACH: {
				text = `${LABELS[PaymentMethodTypeEnum.ACH]} ${last4}`;
				icon = noIcons ? '' : EPaymentIcons[PaymentMethodTypeEnum.ACH];
				break;
			}
			case PaymentMethodTypeEnum.NOT_SET: {
				text = LABELS[PaymentMethodTypeEnum.NOT_SET];
				icon = noIcons ? '' : EPaymentIcons[PaymentMethodTypeEnum.NOT_SET];
				break;
			}
			case PaymentMethodTypeEnum.CASH: {
				text = LABELS[PaymentMethodTypeEnum.CASH];
				icon = noIcons ? '' : EPaymentIcons[PaymentMethodTypeEnum.CASH];
				break;
			}
			case PaymentMethodTypeEnum.BALANCE: {
				text = LABELS[PaymentMethodTypeEnum.BALANCE];
				icon = noIcons ? '' : EPaymentIcons[PaymentMethodTypeEnum.BALANCE];
				break;
			}
			case PaymentMethodTypeEnum.OTHER:
			default: {
				text = LABELS[PaymentMethodTypeEnum.OTHER];
				icon = noIcons ? '' : EPaymentIcons[PaymentMethodTypeEnum.OTHER];
				break;
			}
		}

		return { text, icon, iconSize };
	}, [normalizedType, rawType, pmId, noIcons, state.ccBrand, state.ccLast4]);

	return <PaymentDetailAndIcon text={text} height={DEFAULT_LINE_HEIGHT} icon={icon} color={color} iconSize={iconSize} />;
};

export { PaymentMethodTypeEnum };
